import React, { useState, useEffect } from 'react';
import { categoryService } from '../../api/categoryService';
import '../Common/Common.css';

/**
 * Build a depth-ordered list of categories for an indented <select>, so an
 * arbitrarily deep category tree can be browsed. When editing a category we
 * exclude it and all of its descendants to prevent creating a cycle.
 */
function buildIndentedCategoryOptions(categories, excludeId) {
  const excluded = new Set();
  if (excludeId != null) {
    const collect = (id) => {
      excluded.add(id);
      categories
        .filter((c) => String(c.parent_id) === String(id))
        .forEach((c) => collect(c.id));
    };
    collect(excludeId);
  }

  const childrenByParent = new Map();
  categories.forEach((c) => {
    const key = c.parent_id == null ? 'root' : String(c.parent_id);
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(c);
  });

  const out = [];
  const walk = (key, depth) => {
    (childrenByParent.get(key) || []).forEach((cat) => {
      if (excluded.has(cat.id)) return;
      out.push({ ...cat, depth });
      walk(String(cat.id), depth + 1);
    });
  };
  walk('root', 0);
  return out;
}

function CategoryForm({ category, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    parent_id: '',
    display_order: 0,
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        parent_id: category.parent_id || '',
        display_order: category.display_order ?? 0,
      });
    }
  }, [category]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategories({ per_page: 100 });
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        display_order: Number(formData.display_order) || 0,
      };
      if (category) {
        await categoryService.updateCategory(category.id, payload);
      } else {
        await categoryService.createCategory(payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label>Category Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Slug *</label>
        <input
          type="text"
          name="slug"
          value={formData.slug}
          onChange={handleChange}
          required
          placeholder="category-slug"
        />
      </div>

      <div className="form-group">
        <label>Parent Category (optional)</label>
        <select name="parent_id" value={formData.parent_id} onChange={handleChange}>
          <option value="">None (Top Level)</option>
          {buildIndentedCategoryOptions(categories, category?.id).map((cat) => (
            <option key={cat.id} value={cat.id}>
              {`${'   '.repeat(cat.depth)}${cat.depth > 0 ? '└ ' : ''}${cat.name}`}
            </option>
          ))}
        </select>
        <small style={{ color: '#888' }}>
          Laissez vide pour une catégorie de premier niveau. Vous pouvez créer
          autant de sous-niveaux que nécessaire.
        </small>
      </div>

      <div className="form-group">
        <label>Ordre d'affichage</label>
        <input
          type="number"
          name="display_order"
          value={formData.display_order}
          onChange={handleChange}
          min="0"
          step="1"
        />
        <small style={{ color: '#888' }}>
          Contrôle l'ordre des catégories sur le site. Les plus petits nombres
          apparaissent en premier (0 = en tête).
        </small>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export default CategoryForm;
